import React from "react";
import useRouter from "use-react-router";
import { option, function as fn, apply } from "fp-ts";
import { useTranslation } from "react-i18next";
import { Gig, GigReportOverviewFragment, GigReportStepsQuery, GigReportStepsQueryVariables } from "gql-gen";
import { Switch, Route } from "react-router";
import { useQuery } from "@apollo/client";

import { ICell } from "modules/Connection/types";
import icon from "assets/report.svg";
import Text from "components/Text";
import Report from "modules/Report";
import { UserInfo, useUserInfo } from "modules/Dashboard/UserInfo";
import Spinner from "components/Spinner";

import Blurred from "../Blurred";
import FieldReportView from "./FieldReportView";

import styles from "./styles.scss";
import fragment from "./report.gql";
import gigReportStepsQuery from "../../../../../Report/reportSteps.gql";
import { isEditable } from "utilities/gigAccess";

export { EditReportLinks } from "./EditReportLinks";

interface ReportOverviewComponentProps {
  data?: GigReportOverviewFragment | null;
}

const ReportOverviewComponent: React.FC<ReportOverviewComponentProps> = ({ data }) => {
  const userInfo = useUserInfo();
  const iAmTalentOrg = fn.pipe(
    apply.sequenceS(option.Apply)({
      userOrgId: option.fromNullable(userInfo.me?.organizationUser?.organizationId),
      talentOrgId: option.fromNullable(data?.talentOrganization?.id),
    }),
    option.exists(({ userOrgId, talentOrgId }) => userOrgId === talentOrgId),
  );

  const { data: gigReportStepsData, loading: gigReportStepsLoading } = useQuery<
    GigReportStepsQuery,
    GigReportStepsQueryVariables
  >(gigReportStepsQuery, {
    variables: { gigId: data?.id || "" },
    skip: !data?.id,
  });

  const { t } = useTranslation();
  const { match, location, history } = useRouter();
  const isEditing = location.pathname.includes("/+edit-report");

  if (userInfo.loading || !data || gigReportStepsLoading) return <Spinner />;

  const { gigIsVerified } = isEditable(data as Gig, userInfo, 'dashboard');

  return (
    <div className={styles.reportOverview}>
      <Switch>
        <Route
          path={`${match.path}/:vpage*/+edit-report`}
          render={props => (
            <Report
              {...props}
              gig={data as Gig}
              userInfo={userInfo}
              gigRefetch={() => Promise.resolve()}
              verifyReport
              onSaveAndClose={() =>
                history.push({ ...location, pathname: `${match.url}/${props.match.params.vpage}` })
              }
            />
          )}
        />
        <Route
          path={`${match.path}/:vpage*`}
          render={() => {
            const sectionIsVisible = iAmTalentOrg || gigIsVerified;

            return (
              <Blurred
                blur={!sectionIsVisible}
                link={
                  <a href="https://help.gopinata.com/en/articles/4231577" target="_blank">
                    {t("gigs.cells.reportHelp")}
                  </a>
                }
                bannerText={t(`gigs.cells.blurredSectionBanner`)}
              >
                <div className={styles.viewReport}>
                  <FieldReportView
                    loadingGigDetails={!data}
                    gig={data && data.report ? data : null}
                    gigReportSteps={gigReportStepsData?.gigReportSteps || null}
                  />
                </div>
              </Blurred>
            );
          }}
        />
      </Switch>
    </div>
  );
};

export const ReportOverviewCell: ICell<GigReportOverviewFragment> = {
  component: ReportOverviewComponent as any,
  icon,
  fragment,
};
